import React from "react";
import GeotabDayPicker from "./GeotabDayPicker.jsx";
import { fetchGeotabCalibration, forgetGeotabCalibrationChoices, saveGeotabReviewDecision, simulateGeotabCalibration } from "../../api/geotabApi.js";
import { getLocalTodayDispatchDate } from "../../utils/dispatchDayCalendarUtils.js";
import GeotabBusy from "./GeotabBusy.jsx";
import GeotabAdjustments, { ApplyDialog } from "./GeotabAdjustments.jsx";
import GeotabBadge from "./GeotabBadge.jsx";
import GeotabDriversSection from "./GeotabDriversSection.jsx";
import { Hint, HoverRows } from "./GeotabHover.jsx";
import GeotabMap from "./GeotabMap.jsx";
import GeotabPlanBar from "./GeotabPlanBar.jsx";
import { deltaTone, fmtDelta, fmtDistance, fmtDuration } from "./geotabFormat.js";
import { LINK_CLASS as LINK } from "./geotabLinks.js";
import { THRESHOLD_CHOICES, readGeotabSession, writeGeotabSession } from "./geotabPreferences.js";

const CELL = "border-t border-slate-100 px-2 py-1 text-[12.5px] tabular-nums";
const HEAD = "px-2 py-1 text-left text-[10.5px] font-bold uppercase tracking-wide text-slate-500";
const SEP = "border-l border-slate-200";
const TABS = [["adjust", "À ajuster"], ["mills", "Meuneries"], ["trucks", "Camions"], ["route", "Route"], ["drivers", "Chauffeurs"], ["check", "À vérifier"]];

const GAP_TABS = ["mills", "trucks", "route", "drivers"];

// Écart toléré des onglets : sur la cible jusqu'au choix ; au-delà, plus long que prévu en jaune puis rouge,
// plus court en violet puis violet foncé (au-delà du double du choix).
function GapLegend({ gap, onChange }) {
  const chip = (tone, text) => <span className="inline-block"><GeotabBadge tone={tone}>{text}</GeotabBadge></span>;
  return (
    <span className="flex flex-wrap items-center gap-2 py-0.5 text-[11.5px] text-slate-600">
      <label className="inline-flex items-center gap-1 font-semibold text-slate-700">Écart toléré
        <select aria-label="Écart toléré" value={gap} onChange={(e) => onChange(Number(e.target.value))}
          className="h-6 rounded-[4px] border border-slate-300 bg-white py-0 pl-1.5 pr-6 text-[12px] font-semibold text-slate-800">
          {THRESHOLD_CHOICES.gap_min.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
        </select>
      </label>
      {chip("ok", `Sur la cible ± ${gap} min`)}
      <span className="font-semibold text-slate-700">Plus long que prévu</span>{chip("warn", `+${gap + 1} à +${2 * gap}`)}{chip("bad", `plus de +${2 * gap}`)}
      <span className="font-semibold text-slate-700">Plus court que prévu</span>{chip("early", `−${gap + 1} à −${2 * gap}`)}{chip("early2", `plus de −${2 * gap}`)}
    </span>
  );
}

function shiftDate(value, days) {
  const d = new Date(`${value}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function Section({ title, hint, action, children }) {
  return (
    <section className="min-w-0 overflow-x-auto rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-1">
        <span className="text-[12.5px] font-bold text-slate-800">{title}</span>
        <span className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">{hint}{action}</span>
      </div>
      {children}
    </section>
  );
}

// Colonne Suggéré : en gras la valeur qu'« À ajuster » propose, sinon « inchangé ».
function settingText(current, after, unit) {
  if (current == null && after == null) return "—";
  if (after == null || Math.round(after) === Math.round(current)) return <span className="text-slate-400">inchangé</span>;
  return <b className="text-slate-900">{`${Math.round(after)} ${unit}`}</b>;
}

// Écart à côté du réel : réel − prévu en minutes arrondies, pour que le calcul de la bulle s'additionne.
const diff = (real, plan) => (real == null || plan == null ? null : Math.round(real) - Math.round(plan));

const gapChip = (minutes, tolerance, tone = deltaTone(minutes, tolerance)) => (
  <span className="inline-block w-16 shrink-0 align-middle"><GeotabBadge tone={tone}>{fmtDelta(minutes)}</GeotabBadge></span>
);

// Bulle de calcul, ouverte au survol de la valeur soulignée.
const withCalc = (title, rows, children) => (
  <HoverRows title={title} rows={rows}><span className="cursor-help underline decoration-dotted underline-offset-2">{children}</span></HoverRows>
);

// Standard des onglets : les colonnes de la ligne, puis « Actuel » et « Suggéré », chacun le réglage et son écart, ou les groupes
// donnés ([nom, colonnes]). Une étiquette peut être [texte, infobulle].
// Un réglage commun à toutes les lignes est écrit au-dessus du tableau : il ne reste alors que l'écart.
function StandardHead({ lead, setting, noGap = false, groups: given }) {
  const sub = [setting, !noGap && "Écart"].filter(Boolean);
  const groups = given || [["Actuel", sub], ["Suggéré", sub]];
  // Première colonne de chaque groupe : trait de séparation.
  const starts = new Set(groups.map((_, g) => groups.slice(0, g).reduce((n, [, c]) => n + c.length, lead.length)));
  const label = (item) => (Array.isArray(item)
    ? <Hint text={item[1]}><span className="cursor-help underline decoration-dotted underline-offset-2">{item[0]}</span></Hint> : item);
  return (
    <thead>
      <tr>
        <th className={HEAD} colSpan={lead.length} />
        {groups.map(([name, c]) => <th key={name} className={`${HEAD} ${SEP}`} colSpan={c.length}>{name}</th>)}
      </tr>
      <tr>{[...lead, ...groups.flatMap(([, c]) => c)].map((item, i) => (
        <th key={i} className={`${HEAD} ${starts.has(i) ? SEP : ""}`}>{label(item)}</th>
      ))}</tr>
    </thead>
  );
}

// Réglage commun, écrit une fois : actuel, puis suggéré.
const Common = ({ label, now, after, unit, feminine = false }) => (
  <span>{`${label} : ${feminine ? "actuelle" : "actuel"} `}<b className="tabular-nums">{now == null ? "—" : `${Math.round(now)} ${unit}`}</b>
    {` · ${feminine ? "suggérée" : "suggéré"} `}{settingText(now, after, unit)}</span>
);

const SPEED_HINT = [["Calcul", "km Geotab ÷ temps de conduite"], ["Exemple", "300 km en 5 h → 60 km/h"], ["Exclus", "arrêts, manœuvres de moins de 500 m"]];
// Temps de route du Dispatch : temps fixe par trajet (dès 500 m) + km à la vitesse du camion.
const routeMinutes = (fixed, speed, km) => (km >= 0.5 ? fixed : 0) + (60 * km) / speed;
// Vitesse, et débits d'un dompeur (une seule pièce) : réglages du camion.
const TRUCKS_LINK = <a className={LINK} href="/data-manager?tab=vehicles">Camions</a>;
// Débits et temps aspirateur : réglages de la remorque (ceux d'un dompeur sont dans Camions).
const TRAILERS_LINK = <a className={LINK} href="/data-manager?tab=compartments">Remorques</a>;

// Infobulles des en-têtes du tableau des meuneries.
const MILL_HINTS = {
  dispatch: "Temps sur place du plan de chaque journée, tel que dans le Gantt du Dispatch. Médiane des visites.",
  real: "Médiane du temps sur place mesuré par Geotab, Hors service du chauffeur compris (dîner aussi) : le camion est sur place. Retirée : l'attente avant l'ouverture.",
  gap: "Réel − Prévu.",
  current: "Visites dont le plan du Dispatch utilisait déjà le supplément, le temps fixe et le débit d'aujourd'hui, et leur écart mesuré. Au survol de l'écart : chaque réglage passé.",
  plan: "Temps du site + tonnes au débit de la remorque, avec les réglages d'aujourd'hui : ce que le calibrage corrige. Médiane des visites.",
  site: "Temps fixe commun + supplément de la meunerie, une fois « À ajuster » appliqué.",
};
// Journée « 2026-10-09 » → « 9 oct. », à midi pour ne pas reculer d'un jour.
const dayOf = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short" });
// Écart au réglage actuel en gris sous 3 journées : une journée lente ou rapide pèse encore trop.
const CURRENT_MIN_DAYS = 3;

// Chargement = temps du site (temps fixe commun + supplément de la meunerie) + tonnes au débit de la remorque.
// Une meunerie de 10 visites et plus a son propre temps, plus court ou plus long que le temps commun (supplément négatif ou positif).
// Chaque ligne se lit Prévu au Dispatch, Réel, Écart ; puis le prévu avec les réglages d'aujourd'hui, celui que le calibrage corrige.
function MillsTab({ data, gap, showSingleVisits, onPreferencesChange }) {
  const fixedNow = Math.round(data.loading_fixed?.current || 0);
  const fixedAfter = Math.round(data.loading_fixed?.after ?? fixedNow);
  const singleVisitCount = data.loading_sites.filter((s) => s.visits < 2).length;
  // Une seule visite ne permet aucune conclusion : ces sites restent repliés.
  const sites = data.loading_sites.filter((s) => showSingleVisits || s.visits >= 2);
  return (
    <Section title="Temps de chargement par meunerie" hint="Écart = réel − prévu · Prévu au Dispatch : temps sur place du plan de chaque journée, médiane des visites · trié par poids (écart × visites)"
      action={<><a className={LINK} href="/system-config">Paramètres système</a><a className={LINK} href="/data-manager?tab=correspondences">Clients et sites</a></>}>
      <div className="flex flex-wrap items-center gap-2 px-1 pb-1 text-[12px] text-slate-600">
        <HoverRows title="Temps fixe commun" rows={[["Sert aux", "sites de moins de 10 visites, avec leur supplément"], ["Calcul", "la plus petite erreur sur ces sites"],
          ["Meuneries", "10 visites et plus : leur propre temps"]]}>
          <span className="cursor-help"><Common label="Temps fixe commun (sites de moins de 10 visites)" now={fixedNow} after={data.loading_fixed?.after} unit="min" /></span>
        </HoverRows>
        <span>· chaque meunerie de 10 visites et plus a son propre temps</span>
      </div>
      <table className="w-full min-w-0">
        <StandardHead lead={["Meunerie", "Visites", "Jours"]} groups={[
          ["Temps sur place", [["Prévu au Dispatch", MILL_HINTS.dispatch], ["Réel", MILL_HINTS.real], ["Écart", MILL_HINTS.gap]]],
          ["Au réglage actuel · mesuré", [["Visites", MILL_HINTS.current], "Écart"]],
          ["Avec les réglages d'aujourd'hui", [["Prévu", MILL_HINTS.plan], "Écart"]],
          ["Suggéré", [["Temps du site", MILL_HINTS.site]]],
        ]} />
        <tbody>
          {sites.map((s) => {
            const now = Math.round(s.current_extra || 0);
            const after = Math.round(s.extra_after ?? now);
            const gapDispatch = diff(s.real_median, s.plan_dispatch_median);
            const gapNow = diff(s.real_median, s.plan_median);
            const gapAfter = diff(s.real_median, s.plan_suggested);
            const gapCurrent = s.current ? diff(s.current.real_median, s.current.plan_median) : null;
            // Bulle de l'écart au réglage actuel : les journées de chaque réglage du plan, du plus ancien au plus récent, puis l'attendu.
            const when = (h) => (h.current ? `Depuis le ${dayOf(h.first)}` : h.first === h.last ? `Le ${dayOf(h.first)}`
              : `${h.first.slice(0, 7) === h.last.slice(0, 7) ? dayOf(h.first).split(" ")[0] : dayOf(h.first)} au ${dayOf(h.last)}`);
            const fewDays = s.current && s.current.days < CURRENT_MIN_DAYS;
            const history = [fewDays && ["À confirmer", `${s.current.days} journée${s.current.days > 1 ? "s" : ""} au réglage actuel : écart en gris jusqu'à ${CURRENT_MIN_DAYS} journées`],
              ...(s.history || []).map((h) => [when(h), <>{h.current && <b>actuel : </b>}{`${h.fixed ? `${h.fixed} fixes + ` : ""}${h.extra < 0 ? "−" : ""}${Math.abs(h.extra)} suppl. · ${h.tph} t/h · ${h.count} visite${h.count > 1 ? "s" : ""} · `}<b>{fmtDelta(diff(h.real_median, h.plan_median))}</b></>]),
              ["Attendu", `${fmtDelta(gapNow)} avec les réglages d'aujourd'hui, toute la période`]];
            // Part des tonnes : le prévu moins le temps du site, en minutes arrondies pour que le calcul s'additionne.
            const parts = (fixed, extra) => `(${fixed} fixes ${extra < 0 ? "−" : "+"} ${Math.abs(extra)} suppl.)`;
            const site = (fixed, extra) => `${fixed + extra} min du site ${parts(fixed, extra)}`;
            const tons = (plan, fixed, extra) => Math.round(plan) - fixed - extra;
            // Attentes vues sur place : à titre d'information, dans la bulle seulement.
            const waits = (w) => `${w.count} visite(s) sur ${s.visits} · ${fmtDuration(w.median)}`;
            const calc = [
              ["Prévu au Dispatch", `${fmtDuration(s.plan_dispatch_median)}, plan de chaque journée → écart ${fmtDelta(gapDispatch)}`],
              ["Aujourd'hui", `${site(fixedNow, now)} + ${tons(s.plan_median, fixedNow, now)} min de tonnes = ${fmtDuration(s.plan_median)} → écart ${fmtDelta(gapNow)}`],
              s.plan_suggested != null && Math.round(s.plan_suggested) !== Math.round(s.plan_median) && ["Suggéré", `${site(fixedAfter, after)} + ${tons(s.plan_suggested, fixedAfter, after)} min de tonnes = ${fmtDuration(s.plan_suggested)} → écart ${fmtDelta(gapAfter)}`],
              ["Réel", `${fmtDuration(s.real_median)}, médiane de ${s.visits} visite(s)`],
              s.early && ["Avant l'ouverture", `${waits(s.early)}, retiré du réel`],
              s.off_duty && ["Hors service", `${waits(s.off_duty)}, compté dans le réel${s.real_median_without_off_duty == null ? ""
                : ` (sans : ${fmtDuration(s.real_median_without_off_duty)})`}`],
              !s.is_meunerie && ["Ferme", "pas cochée Meunerie : temps fixe commun"],
            ];
            return (
              <tr key={s.name}>
                <td className={`${CELL} max-w-[240px] truncate font-semibold text-slate-800`}><Hint text={s.name}><span>{s.name}</span></Hint></td>
                <td className={CELL}>{s.visits}</td>
                <td className={CELL}>{s.days.length}</td>
                <td className={`${CELL} ${SEP} font-semibold text-slate-900`}>{fmtDuration(s.plan_dispatch_median)}</td>
                <td className={`${CELL} font-semibold text-slate-900`}>{withCalc(s.name, calc, fmtDuration(s.real_median))}</td>
                <td className={CELL}>{gapChip(gapDispatch, gap)}</td>
                <td className={`${CELL} ${SEP}`}>{s.current
                  ? <><b className="text-slate-900">{s.current.count}</b><span className="text-slate-400">{` · depuis le ${dayOf(s.current.first)}`}</span></>
                  : <span className="text-slate-400">aucune visite</span>}</td>
                <td className={CELL}>{s.current
                  ? <HoverRows title={`${s.name} · écart par réglage`} rows={history}><span className="inline-block cursor-help">{gapChip(gapCurrent, gap, fewDays ? "mute" : undefined)}</span></HoverRows>
                  : gapChip(null, gap)}</td>
                <td className={`${CELL} ${SEP} text-slate-600`}>{withCalc(s.name, calc, fmtDuration(s.plan_median))}</td>
                <td className={CELL}>{gapChip(gapNow, gap)}</td>
                {/* Temps du site gardé : le réglage déjà en place, que le Dispatch prévoit déjà. */}
                <td className={`${CELL} ${SEP}`}>{!s.own_time
                  ? <span className="text-slate-400">{`commun · ${s.is_meunerie ? "moins de 10 visites" : "pas cochée Meunerie"}`}</span>
                  : fixedAfter + after === fixedNow + now
                    ? <span className="text-slate-700">{`${fixedNow + now} min déjà en place `}<span className="text-slate-400">{parts(fixedNow, now)}</span></span>
                    : settingText(fixedNow + now, fixedAfter + after, "min")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {singleVisitCount > 0 && (
        <label className="mt-1 inline-flex cursor-pointer items-center gap-2 px-1 text-[12px] text-slate-600">
          <input type="checkbox" checked={showSingleVisits} onChange={(e) => onPreferencesChange({ calibration_single_visits: e.target.checked })} className="accent-[var(--color-accent)]" />
          Afficher les {singleVisitCount} sites vus une seule fois
        </label>
      )}
    </Section>
  );
}

// Livraison = temps fixe par visite (Paramètres système) + tonnes au débit de la remorque (Remorques ; un dompeur, le sien dans Camions).
function DeliverySection({ delivery, rates, gap }) {
  const types = Object.keys(delivery.bands || {});
  const [selected, setSelected] = React.useState(types[0]);
  const type = types.includes(selected) ? selected : types[0];
  const fixedNow = Math.round(delivery.current_fixed || 0);
  const fixedAfter = Math.round(delivery.after_fixed ?? fixedNow);
  const rateOf = type === "dompeur" ? "du dompeur" : "de la remorque";
  // Débits des remorques de ce type, actuels et suggérés.
  const ofType = rates.filter((r) => r.unloading.type === type);
  const list = (values) => [...new Set(values.filter(Boolean).map(Math.round))].sort((a, b) => a - b).join(" / ");
  const nowRates = list(ofType.map((r) => r.unloading.param_tph));
  const afterRates = list(ofType.map((r) => r.unloading.after_tph));
  return (
    <Section title="Temps de livraison selon les tonnes" hint={`Prévu = temps fixe + tonnes ÷ débit ${rateOf} · aspirations exclues`}>
      <div className="flex flex-wrap items-center gap-2 px-1 pb-1 text-[12px] text-slate-600">
        <span><Common label="Temps fixe par livraison" now={fixedNow} after={delivery.after_fixed} unit="min" />
          {` · débit des ${type}s : actuel `}<b className="tabular-nums">{nowRates ? `${nowRates} t/h` : "—"}</b>{" · suggéré "}
          {afterRates === nowRates ? <span className="text-slate-400">inchangé</span> : <b className="text-slate-900">{`${afterRates} t/h`}</b>}</span>
        {types.length > 1 && (
          <span className="ml-auto inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Type de camion">
            {types.map((key) => (
              <button key={key} type="button" aria-pressed={type === key} onClick={() => setSelected(key)}
                className={`h-6 px-2 text-[11.5px] font-semibold ${type === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{`${key}s`}</button>
            ))}
          </span>
        )}
      </div>
      <table className="w-full min-w-0">
        <StandardHead lead={["Tonnes", "Livraisons", "Réel"]} />
        <tbody>
          {(delivery.bands?.[type] || []).filter((b) => b.count).map((b) => {
            const calc = [
              b.plan_median != null && ["Actuel", `${fixedNow} min fixes + tonnes au débit ${rateOf} = ${fmtDuration(b.plan_median)} → écart ${fmtDelta(diff(b.real_median, b.plan_median))}`],
              b.model_median != null && ["Suggéré", `${fixedAfter} min fixes + tonnes au débit ${rateOf} = ${fmtDuration(b.model_median)} → écart ${fmtDelta(diff(b.real_median, b.model_median))}`],
              ["Réel", `${fmtDuration(b.real_median)}, médiane de ${b.count} livraisons`],
            ];
            return (
              <tr key={b.label}>
                <td className={`${CELL} font-semibold text-slate-800`}>{b.label}</td>
                <td className={CELL}>{b.count}</td>
                <td className={CELL}>{fmtDuration(b.real_median)}</td>
                <td className={`${CELL} ${SEP}`}>{withCalc(b.label, calc, gapChip(diff(b.real_median, b.plan_median), gap))}</td>
                <td className={`${CELL} ${SEP}`}>{withCalc(b.label, calc, gapChip(diff(b.real_median, b.model_median), gap))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}

const CRUISE_GAP_PCT = 10; // écart de croisière sur la cible jusqu'à ± 10 %, au-delà du double en teinte foncée
const CRUISE_MIN_DAYS = 3; // en gris sous 3 jours : une journée lente ou rapide pèse trop
// Croisière de Geotab : km ÷ (temps de conduite − temps fixe compté par trajet prévu), comparable à la vitesse du camion.
const cruiseKmh = (km, minutes, legs, fixed) => (minutes - legs * fixed > 0 ? (60 * km) / (minutes - legs * fixed) : null);
const fmtPct = (value) => {
  const r = Math.round(value);
  return `${r > 0 ? "+" : r < 0 ? "−" : ""}${Math.abs(r)} %`;
};
const fmtKm = (km) => `${Math.round(km).toLocaleString("fr-CA")} km`;

// Vitesse de croisière : la moyenne Geotab, puis la croisière qu'elle donne sans le temps fixe par trajet, face à la vitesse
// réglée. Commune à tous les camions : l'écart d'un camion est montré, sans proposition ; « À ajuster » propose, ou dit pourquoi.
function CruiseSection({ data, onAdjust }) {
  const fixed = data.trip_fixed || 0;
  const speeds = data.speeds || [];
  const sum = (key) => speeds.reduce((total, s) => total + s[key], 0);
  const fleet = { truck: "Toute la flotte", fleet: true, days: sum("days"), km: sum("km"), minutes: sum("minutes"), legs: sum("legs"),
    param_kmh: data.route_model?.current_kmh ?? speeds[0]?.param_kmh };
  const rows = [...speeds, ...(fleet.minutes > 0 ? [fleet] : [])].map((s) => {
    const cruise = cruiseKmh(s.km, s.minutes, s.legs, fixed);
    return { ...s, average: (60 * s.km) / s.minutes, cruise, pct: cruise && s.param_kmh ? 100 * (cruise / s.param_kmh - 1) : null };
  });
  const total = rows.find((s) => s.fleet);
  // Teinte de l'écart affiché : plus lent que le réglage comme un trajet plus long que prévu.
  const tone = (s) => (!s.fleet && s.days < CRUISE_MIN_DAYS ? "mute" : deltaTone(-Math.round(s.pct), CRUISE_GAP_PCT));
  const route = data.adjustments?.find((g) => g.key === "route");
  const proposed = route?.changes.find((c) => c.field === "avg_speed_kmh");
  const held = route?.held?.some((c) => c.field === "avg_speed_kmh");
  const kmh = (value) => (value ? `${Math.round(value)} km/h` : "—");
  return (
    <Section title="Vitesse de croisière" hint="Écarts de temps de route : onglet Route" action={TRUCKS_LINK}>
      {total?.cruise != null && (
        <p className="m-0 px-1 pb-1 text-[12.5px] text-slate-800">
          Geotab : <b>{kmh(total.average)}</b> de moyenne.{fixed > 0 && <>{" Sans les "}<b>{`${Math.round(fixed)} min`}</b>{" que le Dispatch compte par trajet : "}
            <b>{kmh(total.cruise)}</b>{" de croisière,"}</>}{" pour "}<b>{kmh(total.param_kmh)}</b>{" réglés. "}
          {total.pct != null && (Math.abs(Math.round(total.pct)) <= CRUISE_GAP_PCT ? "Le réglage concorde." : `Écart de ${fmtPct(total.pct)}.`)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 px-1 pb-1 text-[12px] text-slate-600">
        <span>{proposed ? `Croisière commune à tous les camions : ${Math.round(proposed.suggested)} km/h proposée dans « À ajuster ».`
          : `Croisière commune à tous les camions : aucune proposée${held ? ", la raison est dans « À ajuster »" : ""}.`}</span>
        {onAdjust && <button type="button" onClick={onAdjust} className={`${LINK} ml-auto`}>Voir dans À ajuster</button>}
      </div>
      <p className="m-0 px-1 pb-1 text-[11.5px] font-semibold text-accent">
        {`Croisière Geotab = km ÷ (temps de conduite − ${Math.round(fixed)} min par trajet prévu) · écart = croisière Geotab ÷ croisière réglée − 1 · l'écart d'un camion est montré sans proposition · en gris sous ${CRUISE_MIN_DAYS} jours.`}
      </p>
      <table className="w-full min-w-0">
        <StandardHead lead={["Camion", "Jours"]} groups={[
          ["Geotab", [["Moyenne", "km Geotab ÷ temps de conduite, manœuvres sur place exclues."],
            ["Croisière", `La moyenne Geotab sans les ${Math.round(fixed)} min que le Dispatch compte par trajet prévu : elle se compare à la vitesse réglée.`]]],
          ["Réglage", [["Croisière", "Vitesse du camion une fois lancé, dans l'onglet Camions : la même pour tous."]]],
          ["", [["Écart", `Croisière Geotab ÷ croisière réglée − 1. Sur la cible à ± ${CRUISE_GAP_PCT} % ; en gris sous ${CRUISE_MIN_DAYS} jours.`]]],
        ]} />
        <tbody>
          {rows.map((s) => (
            <tr key={s.truck} className={s.fleet ? "font-semibold" : undefined}>
              <td className={`${CELL} font-semibold text-slate-800`}>{s.truck}</td>
              <td className={CELL}>{s.days}</td>
              <td className={`${CELL} ${SEP} text-slate-600`}><HoverRows title={s.truck} rows={SPEED_HINT}>
                <span className="cursor-help underline decoration-dotted underline-offset-2">{kmh(s.average)}</span></HoverRows></td>
              <td className={`${CELL} font-semibold text-slate-900`}>{s.cruise == null ? "—" : withCalc(`${s.truck} · croisière Geotab`, [
                ["Conduite", `${fmtKm(s.km)} en ${fmtDuration(s.minutes)} → ${kmh(s.average)} de moyenne`],
                ["Moins", `${s.legs} trajets prévus × ${Math.round(fixed)} min = ${fmtDuration(s.legs * fixed)}`],
                ["Croisière", `${fmtKm(s.km)} en ${fmtDuration(s.minutes - s.legs * fixed)} → ${kmh(s.cruise)}`],
                ["Réglage", `${kmh(s.param_kmh)}, commune à tous les camions`],
              ], kmh(s.cruise))}</td>
              <td className={`${CELL} ${SEP}`}>{kmh(s.param_kmh)}</td>
              <td className={`${CELL} ${SEP}`}><span className="inline-block w-16">
                {s.pct == null ? <GeotabBadge tone="empty" /> : <GeotabBadge tone={tone(s)}>{fmtPct(s.pct)}</GeotabBadge>}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

// Réglages des remorques et des camions : un tableau par réglage, au même standard que les autres onglets. Débits et temps
// aspirateur : une ligne par remorque (ligne TRK, quel que soit le camion qui la tirait) ; vitesse : une ligne par camion.
function TrucksTab({ data, gap, onAdjust }) {
  const loadingFixed = Math.round(data.loading_fixed?.after ?? data.loading_fixed?.current ?? 0);
  const delivery = data.delivery || {};
  const asp = data.aspiration || {};
  const types = new Map(data.rates.map((r) => [r.truck_id, r.unloading.type]));
  // Remorques mesurées seulement (« À ajuster » propose le débit à chaque remorque).
  const loading = data.rates.filter((r) => r.loading.count).sort((a, b) => a.trailer.localeCompare(b.trailer));
  const unloading = data.rates.filter((r) => r.unloading.count);
  // Nom de la ligne, et type (citerne, dompeur) de la remorque id ; une ligne de camion (vitesse) n'a pas de type.
  const name = (label, id) => (
    <td className={`${CELL} font-semibold text-slate-800`}>{label}{types.get(id) && <span className="ml-1 text-[11px] font-normal text-slate-500">{types.get(id)}</span>}</td>
  );
  // Débits d'un dompeur (une seule pièce) : dans sa fiche de l'onglet Camions, pas dans Remorques.
  const rateLinks = (rows) => (rows.some((r) => types.get(r.truck_id) === "dompeur")
    ? [" · dompeurs : onglet Camions", <>{TRAILERS_LINK}{TRUCKS_LINK}</>] : ["", TRAILERS_LINK]);
  const [loadingNote, loadingLinks] = rateLinks(loading);
  const [unloadingNote, unloadingLinks] = rateLinks(unloading);
  return (
    <div className="grid min-w-0 gap-2">
      <div className="grid grid-cols-2 items-start gap-2 max-[1300px]:grid-cols-1">
        <Section title="Débit de chargement" hint={`Écart = réel − prévu, médiane des chargements${loadingNote}`} action={loadingLinks}>
          <table className="w-full min-w-0">
            <StandardHead lead={["Remorque", "Chargements"]} setting="Débit" />
            <tbody>
              {loading.map(({ truck_id: id, trailer, loading: l }) => {
                const now = l.param_tph;
                const after = l.after_tph;
                const calc = [
                  now && ["Actuel", `temps fixe + supplément + tonnes à ${Math.round(now)} t/h → écart ${fmtDelta(l.gap_median)}`],
                  after && ["Suggéré", `${loadingFixed} min fixes + supplément + tonnes à ${Math.round(after)} t/h → écart ${fmtDelta(l.model_gap_median)}`],
                  now && after && Math.round(now) !== Math.round(after) && ["Exemple 30 t", `${Math.round(1800 / now)} min de tonnes actuelles, ${Math.round(1800 / after)} min suggérées`],
                ];
                return (
                  <tr key={id}>
                    {name(trailer, id)}
                    <td className={CELL}>{l.count}</td>
                    <td className={`${CELL} ${SEP} text-slate-600`}>{withCalc(`${trailer} · débit de chargement`, calc, now ? `${Math.round(now)} t/h` : "—")}</td>
                    <td className={CELL}>{gapChip(l.gap_median, gap)}</td>
                    <td className={`${CELL} ${SEP}`}>{withCalc(`${trailer} · débit de chargement`, calc, settingText(now, after, "t/h"))}</td>
                    <td className={CELL}>{gapChip(l.model_gap_median, gap)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
        <Section title="Débit de déchargement" hint={`Écart = réel − prévu, médiane des livraisons${unloadingNote}`} action={unloadingLinks}>
          <table className="w-full min-w-0">
            <StandardHead lead={["Remorque", "Livraisons"]} setting="Débit" />
            <tbody>
              {unloading.map(({ truck_id: id, trailer, unloading: u }) => {
                const calc = [
                  u.param_tph && ["Actuel", `${Math.round(delivery.current_fixed || 0)} min fixes + tonnes à ${Math.round(u.param_tph)} t/h → écart ${fmtDelta(u.gap_median)}`],
                  u.after_tph && ["Suggéré", `${Math.round(delivery.after_fixed ?? delivery.current_fixed ?? 0)} min fixes + tonnes à ${Math.round(u.after_tph)} t/h → écart ${fmtDelta(u.model_gap_median)}`],
                  u.param_tph && u.after_tph && Math.round(u.param_tph) !== Math.round(u.after_tph)
                    && ["Exemple 20 t", `${Math.round(1200 / u.param_tph)} min de tonnes actuelles, ${Math.round(1200 / u.after_tph)} min suggérées`],
                ];
                return (
                  <tr key={id}>
                    {name(trailer, id)}
                    <td className={CELL}>{u.count}</td>
                    <td className={`${CELL} ${SEP} text-slate-600`}>{withCalc(`${trailer} · débit de déchargement`, calc, u.param_tph ? `${Math.round(u.param_tph)} t/h` : "—")}</td>
                    <td className={CELL}>{gapChip(u.gap_median, gap)}</td>
                    <td className={`${CELL} ${SEP}`}>{withCalc(`${trailer} · débit de déchargement`, calc, settingText(u.param_tph, u.after_tph, "t/h"))}</td>
                    <td className={CELL}>{gapChip(u.model_gap_median, gap)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
        {data.elevator_deliveries?.some((e) => e.visits >= 2) && (
          <Section title="Livraisons à un lieu de chargement"
            hint={`Élévateur, meunerie ou ferme qui charge aussi : ${data.elevator_deliveries.reduce((n, e) => n + e.visits, 0)} livraisons mesurées à part, hors du débit de déchargement · lieux vus 2 fois et plus`}>
            <table className="w-full min-w-0">
              <thead><tr><th className={HEAD}>Lieu</th><th className={HEAD}>Livraisons</th><th className={HEAD}>Réel</th><th className={HEAD}>Prévu</th><th className={HEAD}>Écart</th></tr></thead>
              <tbody>
                {data.elevator_deliveries.filter((e) => e.visits >= 2).map((e) => (
                  <tr key={e.name}>
                    <td className={`${CELL} max-w-[240px] truncate font-semibold text-slate-800`}><Hint text={e.name}><span>{e.name}</span></Hint></td>
                    <td className={CELL}>{e.visits}</td>
                    <td className={CELL}>{fmtDuration(e.real_median)}</td>
                    <td className={CELL}>{fmtDuration(e.plan_median)}</td>
                    <td className={CELL}>{gapChip(e.gap_median, gap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
        <Section title="Temps aspirateur" hint="Réglage de la remorque · mission = aspiration + livraison" action={TRAILERS_LINK}>
          <div className="px-1 pb-1 text-[12px] text-slate-600">
            <Common label="Débit d'aspiration, tous les camions" now={asp.current_rate_tph || 0} after={asp.after_rate_tph} unit="t/h" />
          </div>
          <table className="w-full min-w-0">
            <StandardHead lead={["Remorque", "Aspirations", "Réel"]} setting="Temps" />
            <tbody>
              {data.aspirations.map((a) => {
                const calc = [
                  ["Actuel", `${Math.round(a.param_min)} min${asp.current_rate_tph ? ` + tonnes à ${asp.current_rate_tph} t/h` : ""} → écart ${fmtDelta(a.gap_today)}`],
                  ["Suggéré", `${Math.round(a.after_min)} min${asp.after_rate_tph ? ` + tonnes à ${asp.after_rate_tph} t/h` : ""} → écart ${fmtDelta(a.gap_suggested)}`],
                  ["Réel", `${fmtDuration(a.real_median)} pour ${a.tons_median} t (médianes), aspiration + livraison`],
                ];
                return (
                  <tr key={a.truck_id}>
                    {name(a.trailer, a.truck_id)}
                    <td className={CELL}>{a.count}</td>
                    <td className={CELL}>{fmtDuration(a.real_median)}</td>
                    <td className={`${CELL} ${SEP} text-slate-600`}>{withCalc(`${a.trailer} · temps aspirateur`, calc, `${Math.round(a.param_min)} min`)}</td>
                    <td className={CELL}>{gapChip(a.gap_today, gap)}</td>
                    <td className={`${CELL} ${SEP}`}>{withCalc(`${a.trailer} · temps aspirateur`, calc, settingText(a.param_min, a.after_min, "min"))}</td>
                    <td className={CELL}>{gapChip(a.gap_suggested, gap)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
        <CruiseSection data={data} onAdjust={onAdjust} />
      </div>
      {data.delivery?.count > 0 && <DeliverySection delivery={data.delivery} rates={data.rates} gap={gap} />}
    </div>
  );
}

const FERRY_HEAD = ["Trajet", "Prévu", "Nb", "Réel", "Arrêt au traversier", "Nb", "Réel", "Traversier − route"];

// Mêmes paires de sites d'une rive à l'autre : passage au traversier ou par la route.
function FerrySection({ ferry, tolerance }) {
  const gap = (m) => (m.count ? `${fmtDelta(m.gap_median)} (${m.count})` : "—");
  return (
    <Section title="Traversier ou route" hint="Trajets d'une rive à l'autre · médianes, sans les arrêts hors plan · le Dispatch prévoit la route">
      {ferry.pairs.length ? (
        <>
          <p className="px-1 pb-1 text-[12px] text-slate-600">
            Écart médian au prévu : traversier <b className="tabular-nums">{gap(ferry.ferry)}</b> · route <b className="tabular-nums">{gap(ferry.road)}</b>
          </p>
          <table className="w-full min-w-0">
            <thead>
              <tr><th className={HEAD} colSpan={2} /><th className={`${HEAD} ${SEP}`} colSpan={3}>Traversier</th><th className={`${HEAD} ${SEP}`} colSpan={2}>Route</th><th className={`${HEAD} ${SEP}`} /></tr>
              <tr>{FERRY_HEAD.map((label, i) => <th key={i} className={`${HEAD} ${[2, 5, 7].includes(i) ? SEP : ""}`}>{label}</th>)}</tr>
            </thead>
            <tbody>
              {ferry.pairs.map((p) => (
                <tr key={`${p.from}-${p.to}`}>
                  <td className={`${CELL} max-w-[240px] truncate font-semibold text-slate-800`}><Hint text={`${p.from} ↔ ${p.to}`}><span>{`${p.from} ↔ ${p.to}`}</span></Hint></td>
                  <td className={`${CELL} text-slate-500`}>{fmtDuration(p.ferry.plan_median)}</td>
                  <td className={`${CELL} border-l border-slate-100`}>{p.ferry.count}</td>
                  <td className={CELL}><HoverRows title="Par le traversier" rows={[["Total", fmtDuration(p.ferry.real_median)], ["Au traversier", `${fmtDuration(p.ferry.wait_median)} (attente + traversée)`], ["Retirés", "arrêts hors plan"]]}>
                    <span className="cursor-help underline decoration-dotted underline-offset-2">{fmtDuration(p.ferry.real_median)}</span></HoverRows></td>
                  <td className={`${CELL} text-slate-500`}>{fmtDuration(p.ferry.wait_median)}</td>
                  <td className={`${CELL} border-l border-slate-100`}>{p.road.count || "—"}</td>
                  <td className={CELL}>{p.road.count ? fmtDuration(p.road.real_median) : "—"}</td>
                  <td className={`${CELL} border-l border-slate-100`}><span className="inline-block w-16">
                    {p.road.count ? <GeotabBadge tone={deltaTone(p.ferry.real_median - p.road.real_median, tolerance)}>{fmtDelta(p.ferry.real_median - p.road.real_median)}</GeotabBadge> : <GeotabBadge tone="empty" />}
                  </span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : <p className="px-1 text-[12px] text-slate-500">Aucun passage au traversier sur la période.</p>}
    </Section>
  );
}

// Écart d'une tranche : sur la cible sous 3 min ou 10 % du prévu.
const routeTone = (gap, plan) => deltaTone(gap, Math.max(3, 0.1 * plan));

// Temps de route : temps fixe par trajet et vitesse, communs à toutes les tranches, écrits au-dessus ; le tableau montre les écarts.
function RouteTab({ data, gap, onAdjust }) {
  const model = data.route_model;
  const gapBadge = (b, minutes, plan) => (
    <span className="inline-block w-16">{b.legs && minutes != null
      ? <GeotabBadge tone={b.reliable ? routeTone(minutes, plan) : "mute"} title={b.reliable ? undefined : "Moins de 3 trajets"}>{fmtDelta(minutes)}</GeotabBadge>
      : <GeotabBadge tone="empty" />}</span>
  );
  const examples = model ? [10, 60, 250].map((km) => [`${km} km`, `${fmtDuration(routeMinutes(model.after_fixed, model.speed_kmh, km))}${
    model.current_kmh ? ` (actuel ${fmtDuration(routeMinutes(model.current_fixed || 0, model.current_kmh, km))})` : ""}`]) : [];
  return (
    <div className="grid grid-cols-2 items-start gap-2 max-[1300px]:grid-cols-1">
      <Section title="Temps de route selon la distance" hint="Médiane par trajet · sans les arrêts hors plan · sur la cible à ± 10 % du prévu (3 min au moins)">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-1 pb-1 text-[12px] text-slate-600">
          {model ? (
            <>
              <HoverRows title="Temps de route suggéré" rows={[["Calcul", `${model.after_fixed} min fixes + km ÷ ${model.speed_kmh} km/h`], ...examples,
                ["Écart par trajet", `${fmtDuration(model.error_today)} actuel, ${fmtDuration(model.error_suggested)} suggéré`]]}>
                <span className="cursor-help"><Common label="Temps fixe par trajet" now={model.current_fixed || 0} after={model.after_fixed} unit="min" />
                  {" · "}<Common label="vitesse de croisière" now={model.current_kmh} after={model.speed_kmh} unit="km/h" feminine /></span>
              </HoverRows>
              {onAdjust && <button type="button" onClick={onAdjust} className={`${LINK} ml-auto`}>Voir dans À ajuster</button>}
            </>
          ) : <span>Moins de 10 trajets mesurés : pas de réglage suggéré.</span>}
        </div>
        <p className="px-1 pb-1 text-[11.5px] font-semibold text-accent">Écart = médiane des écarts de chaque trajet.</p>
        <table className="w-full min-w-0">
          <StandardHead lead={["Distance (route du Dispatch)", "Trajets", "Arrêts retirés", "Réel"]} />
          <tbody>
            {data.driving.map((b) => {
              // Écart : médiane des écarts de chaque trajet (pas la différence de deux médianes, trompeuse sur les longs trajets).
              const calc = [
                ["Actuel", `${Math.round(model?.current_fixed || 0)} min fixes + km à la vitesse du camion · prévu ${fmtDuration(b.plan_median)} en médiane · écart ${fmtDelta(b.gap_today)}`],
                b.plan_suggested != null && ["Suggéré", `${model.after_fixed} min fixes + km ÷ ${model.speed_kmh} km/h · prévu ${fmtDuration(b.plan_suggested)} en médiane · écart ${fmtDelta(b.gap_suggested)}`],
                ["Réel", `${fmtDuration(b.real_median)}, médiane de ${b.legs} trajets`],
              ];
              return (
                <tr key={b.band}>
                  <td className={`${CELL} font-semibold text-slate-800`}>{b.band}</td>
                  <td className={CELL}>{b.legs}</td>
                  <td className={`${CELL} text-slate-500`}>{b.stopped_legs ? `${b.stopped_legs} · ${fmtDuration(b.stops_minutes)}` : "—"}</td>
                  <td className={CELL}>{b.legs ? fmtDuration(b.real_median) : "—"}</td>
                  <td className={`${CELL} ${SEP}`}>{b.legs ? withCalc(b.band, calc, gapBadge(b, Math.round(b.gap_today), b.plan_median)) : gapBadge(b, null)}</td>
                  <td className={`${CELL} ${SEP}`}>{b.legs && b.gap_suggested != null
                    ? withCalc(b.band, calc, gapBadge(b, Math.round(b.gap_suggested), b.plan_suggested)) : gapBadge(b, null)}</td>
                </tr>
              );
            })}
            {/* Trajets d'une rive à l'autre du Saint-Laurent : attente et traversée comprises au traversier. */}
            {[["traversier", data.ferry?.ferry], ["ponts", data.ferry?.road]].filter(([, m]) => m?.count).map(([label, m]) => (
              <tr key={label}>
                <td className={`${CELL} font-semibold text-slate-800`}>{`Traverser le fleuve · ${label}`}</td>
                <td className={CELL}>{m.count}</td>
                <td className={`${CELL} text-slate-500`}>{label === "traversier" ? `attente ${fmtDuration(m.wait_median)}` : "—"}</td>
                <td className={CELL}>{fmtDuration(m.real_median)}</td>
                <td className={`${CELL} ${SEP}`}>{gapBadge({ legs: m.count, reliable: m.count >= 3 }, Math.round(m.gap_median), m.plan_median)}</td>
                <td className={`${CELL} ${SEP}`}>{gapBadge({ legs: m.count, reliable: m.count >= 3 }, m.gap_suggested == null ? null : Math.round(m.gap_suggested), m.plan_median)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <FerrySection ferry={data.ferry} tolerance={gap} />
    </div>
  );
}

// Classes d'un arrêt récurrent : seul le travail non prévu sort des mesures (ni dîner ni arrêt hors plan).
const STOP_CLASSES = [["fuel", "Carburant"], ["garage", "Garage"], ["mill_wait", "Attente à la meunerie"], ["work", "Travail non prévu"]];
const ADDRESS_DECISIONS = { confirmed: "Adresse confirmée", excluded: "Exclue du calcul" };
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-CA", { day: "numeric", month: "short" }) : "");
const SMALL = "h-6 rounded-[4px] border px-2 text-[12px] font-semibold disabled:opacity-50";

function CheckTab({ data, canDecide, onDecide }) {
  // Adresse examinée : la carte ne montre qu'elle, adresse géocodée et arrêts réels.
  const [focus, setFocus] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const decide = (body) => { setSaving(true); onDecide(body).finally(() => setSaving(false)); };
  const stops = data.recurring_stops.filter((s) => !s.decision);
  const addresses = data.addresses.filter((a) => !a.decision);
  const decided = [
    ...data.addresses.filter((a) => a.decision).map((a) => ({ key: `a|${a.name}`, label: a.name, what: ADDRESS_DECISIONS[a.decision.decision], at: a.decision.at, undo: { name: a.name, decision: null } })),
    ...data.recurring_stops.filter((s) => s.decision).map((s) => ({ key: `s|${s.lat}|${s.lon}`, label: s.zone || `${s.lat.toFixed(3)}, ${s.lon.toFixed(3)}`,
      what: STOP_CLASSES.find(([value]) => value === s.decision.class)?.[1], at: s.decision.at, undo: { lat: s.lat, lon: s.lon, stop_class: null } })),
  ];
  const mapped = focus ? addresses.filter((a) => a.name === focus) : addresses;
  const layers = {
    sites: mapped.map((a) => ({ kind: "delivery", lat: a.lat, lon: a.lon, label: String.fromCharCode(65 + (addresses.indexOf(a) % 26)), title: `${a.name} — adresse géocodée` })),
    realStops: [
      ...mapped.map((a) => ({ lat: a.real_lat, lon: a.real_lon, off: false, title: `${a.name} — arrêts réels (${a.visits})` })),
      ...(focus ? [] : stops.map((s) => ({ lat: s.lat, lon: s.lon, off: true, title: `${s.zone || "Lieu non répertorié"} — ${s.count} arrêts, ${fmtDuration(s.minutes)}` }))),
    ],
    offsetLinks: mapped.map((a) => [[a.lat, a.lon], [a.real_lat, a.real_lon], `${a.name} : ${fmtDistance(a.offset_m)}`]),
  };
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_480px] items-start gap-2 max-[1300px]:grid-cols-1">
      <div className="grid gap-2">
        <p className="px-1 text-[12px] font-semibold text-slate-600">Chaque décision garde sa date ; une adresse confirmée ou exclue et un arrêt classé ne reviennent plus ici.</p>
        <Section title="Arrêts récurrents hors plan" hint="Au moins 2 arrêts au même endroit · nom de la zone Geotab quand elle existe">
          <table className="w-full min-w-0">
            <thead><tr><th className={HEAD}>Lieu</th><th className={HEAD}>Arrêts</th><th className={HEAD}>Temps total</th><th className={HEAD}>Camions</th><th className={HEAD}>Journées</th><th className={HEAD}>Classer</th></tr></thead>
            <tbody>
              {stops.map((s) => (
                <tr key={`${s.lat}-${s.lon}`}>
                  <td className={`${CELL} font-semibold text-slate-800`}>{s.zone || <span className="font-normal text-slate-500">{`${s.lat.toFixed(3)}, ${s.lon.toFixed(3)}`}</span>}</td>
                  <td className={CELL}>{s.count}</td>
                  <td className={CELL}>{fmtDuration(s.minutes)}</td>
                  <td className={`${CELL} max-w-[220px] truncate`}><Hint text={s.trucks.join(", ")}><span>{s.trucks.join(", ")}</span></Hint></td>
                  <td className={CELL}>{s.days.length}</td>
                  <td className={CELL}>
                    <select aria-label={`Classer ${s.zone || "cet arrêt"}`} value="" disabled={!canDecide || saving} onChange={(e) => e.target.value && decide({ lat: s.lat, lon: s.lon, stop_class: e.target.value })}
                      className="h-6 rounded-[4px] border border-slate-300 bg-white py-0 pl-1.5 pr-6 text-[12px] font-semibold text-slate-800 disabled:opacity-50">
                      <option value="">Classer…</option>
                      {STOP_CLASSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
        <Section title="Adresses à vérifier" hint="Le camion s'arrête au même endroit, à plus de 500 m de l'adresse géocodée"
          action={<a className={LINK} href="/data-manager?tab=correspondences">Corriger dans Clients et sites</a>}>
          <table className="w-full min-w-0">
            <thead><tr><th className={HEAD}>Repère</th><th className={HEAD}>Client</th><th className={HEAD}>Écart</th><th className={HEAD}>Visites</th><th className={HEAD}>Emplacement réel</th><th className={HEAD}>Décision</th></tr></thead>
            <tbody>
              {addresses.map((a, i) => (
                <tr key={a.name} className={focus === a.name ? "bg-slate-50" : ""}>
                  <td className={`${CELL} font-bold text-slate-500`}>{String.fromCharCode(65 + (i % 26))}</td>
                  <td className={`${CELL} max-w-[240px] truncate font-semibold text-slate-800`}><Hint text={a.name}><span>{a.name}</span></Hint></td>
                  <td className={CELL}><span className="inline-block w-16"><GeotabBadge tone="warn">{fmtDistance(a.offset_m)}</GeotabBadge></span></td>
                  <td className={CELL}>{a.visits}</td>
                  <td className={CELL}><a className="font-semibold text-accent underline underline-offset-2" target="_blank" rel="noreferrer"
                    href={`https://www.openstreetmap.org/?mlat=${a.real_lat}&mlon=${a.real_lon}#map=17/${a.real_lat}/${a.real_lon}`}>{`${a.real_lat.toFixed(5)}, ${a.real_lon.toFixed(5)}`}</a></td>
                  <td className={`${CELL} whitespace-nowrap`}>
                    <span className="inline-flex gap-1">
                      <button type="button" aria-pressed={focus === a.name} onClick={() => setFocus(focus === a.name ? null : a.name)}
                        className={`${SMALL} ${focus === a.name ? "border-accent bg-accent text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>Examiner</button>
                      <button type="button" disabled={!canDecide || saving} onClick={() => decide({ name: a.name, decision: "confirmed" })}
                        className={`${SMALL} border-accent bg-white text-accent hover:bg-slate-50`}>Confirmer l'adresse</button>
                      <button type="button" disabled={!canDecide || saving} onClick={() => decide({ name: a.name, decision: "excluded" })}
                        className={`${SMALL} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>Exclure du calcul</button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
        {decided.length > 0 && (
          <details className="rounded-[4px] border border-slate-200 bg-white px-3 py-1.5">
            <summary className="cursor-pointer text-[12px] font-bold text-slate-700">Décisions prises ({decided.length})</summary>
            <ul className="m-0 mt-1 list-none p-0">
              {decided.map((d) => (
                <li key={d.key} className="flex items-center gap-2 border-t border-slate-100 py-1 text-[12px] first:border-t-0">
                  <b className="min-w-0 flex-1 truncate text-slate-800">{d.label}</b>
                  <span className="text-slate-600">{d.what}{d.at ? ` · ${fmtDay(d.at)}` : ""}</span>
                  <button type="button" disabled={!canDecide || saving} onClick={() => decide(d.undo)}
                    className={`${SMALL} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>Remettre à vérifier</button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      <GeotabMap layers={layers} fitKey={`${data.from}-${data.to}-${data.period}-${focus || ""}`} className="h-[520px]" />
    </div>
  );
}

export default function GeotabCalibrationView({ period = "day", thresholds, onPeriodChange, preferences, onPreferencesChange, canApply = false }) {
  const yesterday = shiftDate(getLocalTodayDispatchDate(), -1);
  // Période enregistrée pour l'utilisateur ; sans choix, les 30 derniers jours.
  const [range, setRangeState] = React.useState(() => ({
    from: preferences.calibration_from || shiftDate(yesterday, -29), to: preferences.calibration_to || yesterday,
  }));
  const setRange = (next) => {
    setRangeState(next);
    onPreferencesChange({ calibration_from: next.from, calibration_to: next.to });
  };
  // Onglet gardé jusqu'à la déconnexion, comme la journée de Prévu vs Réel.
  const [tab, setTabState] = React.useState(() => (TABS.some(([key]) => key === readGeotabSession().calibration_tab) ? readGeotabSession().calibration_tab : "adjust"));
  const setTab = (next) => {
    setTabState(next);
    writeGeotabSession({ calibration_tab: next });
  };
  const [request, setRequest] = React.useState(0);
  const [state, setState] = React.useState({ data: null, busy: false, error: null });
  // Recalcul attendu par le toast d'« Appliquer » : réglé quand le Calibrage a fini de se recharger.
  const reloadWaiters = React.useRef([]);
  const reload = (update) => new Promise((resolve, reject) => {
    reloadWaiters.current.push({ resolve, reject });
    update();
  });
  // Réglages essayés : restent actifs quand la période change, jusqu'à « Quitter la simulation ».
  const [simulation, setSimulation] = React.useState(null);
  const [applyingSimulation, setApplyingSimulation] = React.useState(false);
  const simulate = (changes) => {
    const byKey = new Map((simulation || []).map((c) => [`${c.kind}|${c.id}|${c.field}`, c]));
    // Une valeur déjà essayée garde sa valeur d'origine comme « avant ».
    changes.forEach((c) => byKey.set(`${c.kind}|${c.id}|${c.field}`, { ...c, current: byKey.get(`${c.kind}|${c.id}|${c.field}`)?.current ?? c.current }));
    setSimulation([...byKey.values()]);
  };

  React.useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, busy: true, error: null }));
    (simulation ? simulateGeotabCalibration(range.from, range.to, period, thresholds, simulation) : fetchGeotabCalibration(range.from, range.to, period, thresholds))
      .then((data) => {
        if (cancelled) return;
        setState({ data, busy: false, error: null });
        reloadWaiters.current.splice(0).forEach((waiter) => waiter.resolve());
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ data: null, busy: false, error });
        reloadWaiters.current.splice(0).forEach((waiter) => waiter.reject(error));
      });
    return () => { cancelled = true; };
    // La période de dates ne se recalcule qu'avec « Calculer » : plusieurs semaines prennent quelques secondes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, period, thresholds, simulation]);

  const { data, busy, error } = state;
  const gap = preferences.gap_min;
  const counts = data ? { adjust: data.adjustments?.filter((g) => g.changes.length).length,
    check: data.recurring_stops.filter((s) => !s.decision).length + data.addresses.filter((a) => !a.decision).length } : {};
  // Décision de À vérifier : enregistrée, puis le Calibrage recalculé avec elle.
  const decide = (body) => saveGeotabReviewDecision(body).then(() => reload(() => setRequest((n) => n + 1)));

  return (
    <div className="grid min-w-0 gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-700">
        <span className="font-semibold">Du</span>
        <GeotabDayPicker label="Début de la période" value={range.from} max={range.to} onChange={(from) => setRange({ ...range, from })} />
        <span className="font-semibold">au</span>
        <GeotabDayPicker label="Fin de la période" value={range.to} min={range.from} onChange={(to) => setRange({ ...range, to })} />
        <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Période du Dispatch">
          {[["day", "Jour"], ["evening", "Soir"]].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={period === key} onClick={() => onPeriodChange(key)}
              className={`h-7 px-3 text-[12.5px] font-semibold ${period === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </span>
        <button type="button" disabled={busy} onClick={() => setRequest((n) => n + 1)}
          className="h-7 rounded-[4px] border border-accent bg-accent px-3 text-[12.5px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400">
          Calculer
        </button>
        <span className="text-[11.5px] text-slate-500">
          {data && !busy
            ? period === "evening" && !data.days
              ? "Soir : aucun plan disponible"
              : `${data.days} journées${data.requested_to ? ` (période limitée à 62 jours : jusqu'au ${data.to})` : ""}${data.days_not_closed ? ` · ${data.days_not_closed} non clôturée(s) incluse(s)` : ""} · ${data.truck_days} camions-jours`
                + ` · ${data.clean_truck_days} propres (${data.day_end.count} avec retour mesuré)`
            : "Journées ouvertes et clôturées (plan disponible)"}
        </span>
      </div>
      {busy && <GeotabBusy title="Calcul du Calibrage…" detail="Analyse des journées et des trajets Geotab" />}
      {error && <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error.message}</p>}
      {simulation && (
        <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[4px] border border-slate-200 bg-white px-3 py-1.5 text-[12px] text-slate-700 shadow-[inset_3px_0_0_#a86a24]">
          <b>{`Simulation : ${simulation.length} réglage(s) essayé(s), rien n'est appliqué.`}</b>
          {data?.simulation && !busy && (
            <span className="tabular-nums">
              {`Écart au réel : chargement ${fmtDuration(data.simulation.baseline.loading_error)} → ${fmtDuration(data.loading_fixed?.error_today)}`}
              {data.simulation.baseline.delivery_error != null && ` · déchargement ${fmtDuration(data.simulation.baseline.delivery_error)} → ${fmtDuration(data.delivery?.error_today)}`}
              {data.simulation.baseline.route_error != null && ` · route ${fmtDuration(data.simulation.baseline.route_error)} → ${fmtDuration(data.route_model?.error_today)}`}
            </span>
          )}
          <span className="ml-auto flex items-center gap-2">
            {canApply && (
              <button type="button" disabled={busy} onClick={() => setApplyingSimulation(true)}
                className="h-6 rounded-[4px] border border-accent bg-accent px-3 text-[12px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400">
                {`Appliquer la simulation (${simulation.length})`}
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => setSimulation(null)}
              className="h-6 rounded-[4px] border border-slate-300 bg-white px-3 text-[12px] font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 disabled:text-slate-400">
              Quitter la simulation
            </button>
          </span>
        </div>
      )}
      {applyingSimulation && (
        <ApplyDialog group={{ title: "Simulation", changes: simulation }} onClose={() => setApplyingSimulation(false)}
          onApplied={() => {
            setApplyingSimulation(false);
            return reload(() => setSimulation(null));
          }} />
      )}
      {data && !busy && (
        <>
          {/* Même barre que Dispatch vs Geotab, sur la période ; même tolérance d'arrivée que Prévu vs Réel. En simulation, les
              horaires passent de la valeur actuelle à la valeur simulée ; le respect du plan ne dépend pas des réglages. */}
          <GeotabPlanBar bar={data.bar} timing={data} scope="period" tolerance={thresholds?.tolerance_min ?? 15}
            onToleranceChange={(value) => onPreferencesChange({ tolerance_min: value })} baseline={data.simulation?.baseline || null} />
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-slate-200">
          <nav className="flex flex-wrap items-center gap-4" role="tablist" aria-label="Sections du Calibrage">
            {TABS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 border-0 border-b-2 px-0.5 py-1 text-[12.5px] font-semibold ${tab === key ? "border-accent text-accent-strong" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
                {label}
                {counts[key] ? <span className="rounded-[4px] bg-accent px-1.5 text-[10.5px] font-bold leading-[16px] text-white tabular-nums">{counts[key]}</span> : null}
              </button>
            ))}
          </nav>
          {GAP_TABS.includes(tab) && <GapLegend gap={gap} onChange={(value) => onPreferencesChange({ gap_min: value })} />}
          </div>
          {tab === "adjust" && (
            <GeotabAdjustments groups={data.adjustments} dayEnd={data.day_end} canApply={canApply} simulating={Boolean(simulation)} onSimulate={simulate}
              prudent={data.prudent && { ...data.prudent, habitual: { route: data.route_model, delivery_fixed: data.delivery?.suggested_fixed } }}
              onApplied={() => reload(() => setRequest((n) => n + 1))}
              onForget={(keys) => forgetGeotabCalibrationChoices(keys).then(() => reload(() => setRequest((n) => n + 1)))
                .catch((error) => setState((s) => ({ ...s, error })))} />
          )}
          {tab === "mills" && <MillsTab data={data} gap={gap} showSingleVisits={preferences.calibration_single_visits} onPreferencesChange={onPreferencesChange} />}
          {tab === "trucks" && <TrucksTab data={data} gap={gap} onAdjust={() => setTab("adjust")} />}
          {tab === "route" && <RouteTab data={data} gap={gap} onAdjust={() => setTab("adjust")} />}
          {tab === "drivers" && (data.drivers ? <GeotabDriversSection drivers={data.drivers} tolerance={gap} period={period} simulating={Boolean(simulation)}
            cleanDays={data.clean_truck_days} truckDays={data.truck_days} />
            : <p className="px-1 text-[12px] text-slate-500">Aucune journée comparée sur la période.</p>)}
          {tab === "check" && <CheckTab data={data} canDecide={canApply} onDecide={decide} />}
        </>
      )}
    </div>
  );
}
